import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid from '@mui/material/Grid';

import Eyebrow from '@/components/Eyebrow/Eyebrow';
import type { DialogContent } from '@/components/FormResultDialog/FormResultDialog';
import Heading from '@/components/Heading/Heading';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import Link from '@/components/Link/Link';
import {
  LeadForm,
  LeadFormStyle,
} from '@/components/TopLevelComponents/LeadForm/LeadForm';
import type { GoogleUrls } from '@/constants/baseUrls';
import type { Contentful, LinkContent } from '@/contentful/contentful-exports';
import type { LeadFormType } from '@/enums/FormType';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import type { ActionModalCF, LeadCreatedModalCF } from '@/types';
import { getLeadFormDialogContent } from '@/utils/leadForm';
import useStyles from './ActionUserInput.styles';
import BulletedList from './BulletedList';

export interface ActionUserInputContent extends Contentful {
  fields: {
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    bulletedList?: string[];
    formHeading?: string;
    ownerQuestion?: string;
    submitButtonText?: string;
    requiresTcpaAcknowledgment?: boolean;
    tcpa: RichTextDocument;
    footerText: string;
    footerLink?: LinkContent;
    leadCreatedModal?: LeadCreatedModalCF | ActionModalCF;
    leadNotInServiceModal?: LeadCreatedModalCF | ActionModalCF;
    campaignId?: string;
    googleSheetUrls?: GoogleUrls;
    solarEdgeSubmissionUrl?: string;
    formType?: LeadFormType;
    dialogContent?: DialogContent;
    overrideCampaignId?: boolean;
    hideEmailInput?: boolean;
    hideHomeOwnershipQuestion?: boolean;
    redirectUrl?: string;
    outOfMarketRedirectUrl?: string;
    shouldRedirectWithParams?: boolean;
    useFullName?: boolean;
  };
}

interface ActionUserInputProps {
  content: ActionUserInputContent;
  sectionPosition?: number;
  componentPosition?: number;
}

export default function ActionUserInput({
  content: {
    fields: {
      eyebrow,
      heading,
      subheading,
      bulletedList,
      formHeading,
      ownerQuestion,
      submitButtonText,
      requiresTcpaAcknowledgment = false,
      tcpa,
      footerText,
      footerLink,
      leadCreatedModal,
      leadNotInServiceModal,
      campaignId,
      googleSheetUrls,
      solarEdgeSubmissionUrl,
      formType,
      overrideCampaignId = false,
      hideEmailInput = false,
      hideHomeOwnershipQuestion = false,
      redirectUrl,
      outOfMarketRedirectUrl,
      shouldRedirectWithParams,
      useFullName = false,
    },
  },
  sectionPosition,
  componentPosition,
}: ActionUserInputProps) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes, cx } = useStyles();
  const { isSmall, isExtraSmall } = useViewPortSize();
  const isMdOrLarger = !isExtraSmall && !isSmall;
  const isFirstComponentOfPage =
    sectionPosition === 0 && componentPosition === 0;

  const successDialogContent = getLeadFormDialogContent(
    leadCreatedModal,
    isMdOrLarger
  );
  const failDialogContent = getLeadFormDialogContent(
    leadNotInServiceModal,
    isMdOrLarger
  );

  return (
    <div className={classes.container}>
      <GridContainer gridProps={{ className: classes.contentContainer }} fixed>
        {/* Text Content */}
        <Grid
          className={classes.textContentContainer}
          size={{
            xs: 12,
            md: 5,
          }}
        >
          <div className={classes.textContent}>
            {eyebrow && <Eyebrow text={eyebrow} isOnDark={false} />}
            {heading && (
              <Heading
                level={isFirstComponentOfPage ? 'h1' : 'h2'}
                classNameOverride={globalClasses.h1}
              >
                {heading}
              </Heading>
            )}
            {subheading && (
              <p className={cx(globalClasses.bodyLg, classes.subheading)}>
                {subheading}
              </p>
            )}
            {bulletedList && (
              <BulletedList
                list={bulletedList}
                className={classes.bulletedList}
              />
            )}
          </div>
        </Grid>

        {isMdOrLarger && <Grid size={1} />}

        {/* Lead Form */}
        <Grid
          className={classes.leadForm}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <LeadForm
            heading={formHeading}
            ownerQuestion={ownerQuestion}
            submitButtonText={submitButtonText}
            requiresTcpaAcknowledgment={requiresTcpaAcknowledgment}
            tcpa={tcpa}
            successDialogContent={successDialogContent}
            failDialogContent={failDialogContent}
            campaignId={campaignId}
            googleSheetUrls={googleSheetUrls}
            solarEdgeSubmissionUrl={solarEdgeSubmissionUrl}
            formType={formType}
            overrideCampaignId={overrideCampaignId}
            leadFormstyle={LeadFormStyle.ACTION_USER_INPUT}
            hideEmailInput={hideEmailInput}
            hideHomeOwnershipQuestion={hideHomeOwnershipQuestion}
            redirectUrl={redirectUrl}
            outOfMarketRedirectUrl={outOfMarketRedirectUrl}
            shouldRedirectWithParams={shouldRedirectWithParams}
            useFullName={useFullName}
          />
        </Grid>
      </GridContainer>
      {/* Footer */}
      <GridContainer>
        <Grid className={classes.footer} size={12}>
          <p className={cx(globalClasses.h4, classes.footerText)}>
            {footerText}
          </p>
          {footerLink && (
            <Link
              href={footerLink.fields.url}
              className={cx(globalClasses.bodyLg, classes.footerLink)}
            >
              {footerLink.fields.text}
            </Link>
          )}
        </Grid>
      </GridContainer>
    </div>
  );
}
